import { createFileRoute } from "@tanstack/react-router";
import { useEffect, type FormEvent } from "react";
import { z } from "zod";
import { useDeclarationInvitation } from "~/hooks/useDeclarationInvitation";
import DeclarationSuccess from "~/components/application/declaration/declaration-success";
import DeclarationError from "~/components/application/declaration/declaration-error";
import DeclarationForm from "~/components/application/declaration/declaration-form";

export const Route = createFileRoute("/$tenantSlug/declaration-invitation")({
  validateSearch: z.object({
    token: z.coerce.string(),
  }),
  component: TenantDeclarationInvitation,
});

function TenantDeclarationInvitation() {
  const { token } = Route.useSearch();


  useEffect(() => {
    document.title = "ProudlyHuman\u2122 - Declaration";
  }, []);

  const {
    tokenData, verifyLoading, verifyError, showSuccess,
    declarationItems, declarationsChecked, allDeclarationsChecked,
    handleDeclarationChange, completeDeclarationMutation, contentType,
  } = useDeclarationInvitation(token);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    completeDeclarationMutation.mutate();
  };

  if (verifyLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (showSuccess) return <DeclarationSuccess />;
  if (verifyError || !tokenData) return <DeclarationError error={verifyError instanceof Error ? verifyError : undefined} />;

  const { collaborator, application, applicantName, organizationName, title } = tokenData!;
  if (collaborator.status === "DECLARATION_COMPLETED") return <DeclarationSuccess alreadyCompleted />;

  return (
    <DeclarationForm
      collaborator={collaborator}
      application={application}
      applicantName={applicantName}
      title={title}
      organizationName={organizationName}
      contentType={contentType}
      declarationItems={declarationItems}
      declarationsChecked={declarationsChecked}
      onDeclarationChange={handleDeclarationChange}
      allDeclarationsChecked={allDeclarationsChecked}
      onSubmit={handleSubmit}
      isSubmitting={completeDeclarationMutation.isPending}
    />
  );
}
